import { Link } from "react-router";

import {
  AttemptsStill,
  ClockStill,
  GradeStill,
  ResultStill,
  RubricStill,
  ScoringStill,
  TeamsStill,
} from "@/components/guide/site-stills";
import type { Chapter } from "@/components/guide/tutorial";

/**
 * Chapter 5: what a run is measured on, how runs are ranked, and what decides whether a run
 * counts at all. Each step sits beside a still of the piece of the site where the number
 * appears, so the reader recognises it later. The rubric shown is the course's suggested one;
 * a challenge's own is on its page.
 */

export const metricsChapter: Chapter = {
  id: "metrics",
  label: "Metrics",
  title: "Metrics",
  intro: "What a run is scored on, how the leaderboard orders runs, and what makes a run count.",
  steps: [
    {
      title: "The rubric",
      body: (
        <>
          Each challenge has a rubric, shown on its page. A goal condition on distance, and often
          heading, must be met for the run to score at all. Standing on the spot but facing the
          wrong way can be a miss. Then blocks of rules award points. Within a block the first
          matching rule pays, and the blocks add up. This example is the course&apos;s suggested
          rubric: 10 points, three each for distance and heading, four for SPL.
        </>
      ),
    },
    {
      title: "Distance and heading",
      body: "Distance is the straight-line distance in metres from the robot to the goal at check-in. Heading is the angle in degrees between the robot's direction and the direction the goal photos face, view 0. Both are shown only for runs that met the goal condition. A miss is told that it missed, not by how much.",
    },
    {
      title: "SPL",
      body: "Success weighted by path length: the shortest possible route's steps, turns included, divided by the steps you used, capped at 1. A miss scores 0. It rewards knowing where you are early, because wandering around to localise costs SPL even if you arrive. Repeating a move for several ticks does not help; every tick is a step.",
    },
    {
      title: "Ranking",
      body: "The leaderboard orders runs by rubric points, then SPL, then distance to the goal. Wall-clock time is shown but never ranks, because it measures your laptop and the network. A team appears once, with its best member's best run.",
    },
    {
      title: "Points and your grade",
      body: "Rubric points are the leaderboard's. Your course grade scales them and may add points for the report, the methodology and an autonomous-solution bonus. The numbers the TAs announce are the ones that count; nothing on this site computes a grade.",
    },
    {
      title: "Attempts",
      body: "An attempt is used as soon as your code connects, however the run ends. Challenges may limit attempts; the challenge page shows how many remain. Only your best result counts, so a bad run costs nothing but the attempt.",
    },
    {
      title: "Teams",
      body: (
        <>
          Graded challenges are entered as a team (a team of one is allowed), and only after team
          changes close. Form your team on{" "}
          <Link to="/team" className="text-primary underline-offset-4 hover:underline">
            My team
          </Link>{" "}
          before that date. Attempts are then shared by the team.
        </>
      ),
    },
    {
      title: "Sessions",
      body: "A session lasts one hour. If your connection drops, the run waits five minutes while the SDK reconnects automatically. If you closed the program, end the run from its page (Abort) before starting another.",
    },
  ],
  preview: (step) =>
    [
      <RubricStill key="rubric" />,
      <ResultStill key="result" />,
      <ResultStill key="spl" spl />,
      <ScoringStill key="ranking" />,
      <GradeStill key="grade" />,
      <AttemptsStill key="attempts" />,
      <TeamsStill key="teams" />,
      <ClockStill key="clock" />,
    ][step],
};
